import React, { useRef, useContext, useState, useEffect } from 'react'
import styles from './CandidateOutreach.module.css'
import Select from 'react-select'
import { humanReadableTimePassed, safeParseArray } from '../../Helper/helpers'
import { executivesData, useExecutives, getExecutiveFullName } from '../../store/ExecutiveContext';
import { DateRangePicker } from 'react-date-range';
import { enUS } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { BASE_URL_API } from '../../References/urls';
import AuthContext from '../../store/auth-context';
import { Link } from 'react-router-dom'

const CandidateOutreach = () => {
    const pickerRef = useRef(null);
    const authCtx = useContext(AuthContext);
    const hasAccessTo = safeParseArray(authCtx?.hasAccessTo);

    const options =
        authCtx?.userIdentifier === "999964N8406"
            ? executivesData.map(e => ({
                value: e.userID,
                label: getExecutiveFullName(e.userID),
            }))
            : 
            [
                {
                    value: authCtx?.userIdentifier,
                    label: `${getExecutiveFullName(authCtx?.userIdentifier)} (me)`
                },
                ...hasAccessTo.map(e => ({
                    value: e,
                    label: getExecutiveFullName(e),
                }))
            ];

    const [activityData, setActivityData] = useState([]);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedExecutives, setSelectedExecutives] = useState([]);
    const [summaryType, setSummaryType] = useState({ value: '0', label: '0' });
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [isLoading, setIsLoading] = useState(false);
    const [responseData, setResponseData] = useState([])
    const [recentInterests, setRecentInterests] = useState([])

    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date('2026-01-01'),
            endDate: new Date(),
            key: 'selection',
        }
    ]);

    const handleSelect = (ranges) => {
        setDateRange([ranges.selection]);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (pickerRef.current && !pickerRef.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        };

        if (showDatePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDatePicker]);

    const getOutreachResponse = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(BASE_URL_API + "/getOutreachResponse",
                {
                    method: "POST",
                    body: JSON.stringify({
                        token: authCtx.token,
                        dateRange: dateRange,
                        selectedExecutives: selectedExecutives.map(exec => exec.value)
                    })
                }
            );

            if (!response.ok) {
                console.log("Something went wrong : Server Error")
                setResponseData([])
            } else {
                const respo = await response.json()
                if (respo.status === "success") {
                    setResponseData(respo.response)
                } else {
                    if (respo?.message === "token Expired, please login again!") {
                        authCtx.logout()
                    }
                    setResponseData([])
                }
            }
        } catch (error) {
            console.error("Error fetching outreach data:", error);
            setResponseData([])
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        getOutreachResponse()
    }, [selectedExecutives, dateRange])

    const customSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '42px',
            borderColor: '#e2e8f0',
            borderRadius: '8px',
            '&:hover': {
                borderColor: '#cbd5e0'
            }
        }),
        multiValue: (base) => ({
            ...base,
            backgroundColor: '#edf2f7',
            borderRadius: '4px',
        }),
        multiValueLabel: (base) => ({
            ...base,
            color: '#2d3748',
            fontSize: '13px',
        }),
    };

    const getRecentOutreachInterest = async () => {
        const response = await fetch(BASE_URL_API + "/getRecentOutreachInterest",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    selectedExecutives: selectedExecutives
                })
            }
        );

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo.status === "success") {
                // setRecentInterests(respo?.response)
                const newItems = [...respo.response].reverse();

                setRecentInterests((prev) => [
                    ...newItems,
                    ...prev,
                ]);


            } else {
                if (respo?.message === "token Expired, please login again!") {
                    authCtx.logout()
                }
            }
        }
    }

    useEffect(() => {
        let intervalId = null;

        const startPolling = () => {
            if (intervalId) return; // prevent duplicates

            getRecentOutreachInterest(); // initial call

            intervalId = setInterval(() => {
                if (document.visibilityState === "visible") {
                    getRecentOutreachInterest();
                }
            }, 30000);
        };

        const stopPolling = () => {
            if (intervalId) {
                clearInterval(intervalId);
                intervalId = null;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                startPolling();
            } else {
                stopPolling();
            }
        };

        // Start immediately if visible
        if (document.visibilityState === "visible") {
            startPolling();
        }

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            stopPolling();
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, [selectedExecutives]);






    return (
        <div className={styles.container}>
            {/* Header */}
            <div className={styles.headerWrapper}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Candidate Outreach</h1>
                    <p className={styles.pageSubtitle}>Track and manage candidate responses</p>
                </div>
                <div className={styles.hwRightWrapper}>
                    <div className={styles.datePickerWrapper} ref={pickerRef}>
                        <div
                            className={styles.dateInput}
                            onClick={() => setShowDatePicker(prev => !prev)}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                            <span>{dateRange[0].startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — {dateRange[0].endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>

                        {showDatePicker && (
                            <div className={styles.datePickerDropdown}>
                                <DateRangePicker
                                    ranges={dateRange}
                                    onChange={handleSelect}
                                    locale={enUS}
                                    maxDate={new Date()}
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles.executiveSelectWrapper}>
                        <Select
                            options={options}
                            placeholder="Filter by Executive"
                            className={styles.selectBox}
                            styles={customSelectStyles}
                            isMulti
                            isSearchable
                            value={selectedExecutives}
                            onChange={setSelectedExecutives}
                        />
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className={styles.mainContent}>
                {/* Left Side - Outreach Responses */}
                <div className={styles.leftPanel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelTitleWrapper}>
                            <h2 className={styles.panelTitle}>Outreach Responses</h2>
                            <span className={styles.countBadge}>{responseData?.length || 0}</span>
                        </div>
                    </div>

                    <div className={styles.responseList}>
                        {isLoading ? (
                            <div className={styles.loadingState}>
                                <div className={styles.spinner}></div>
                                <p>Loading responses...</p>
                            </div>
                        ) : responseData?.length > 0 ? (
                            responseData.map((item, index) => (
                                <div key={index} className={styles.responseCard}>
                                    <div className={styles.cardHeader}>
                                        <div className={styles.candidateInfo}>
                                            <div className={styles.avatarWrapper}>
                                                <div className={styles.avatar}>
                                                    {item?.stylist_name?.charAt(0).toUpperCase()}
                                                </div>
                                                <div className={styles.onlineIndicator}></div>
                                            </div>
                                            <div className={styles.candidateDetails}>
                                                <h3 className={styles.candidateName}>{item?.stylist_name}</h3>
                                                <div className={styles.locationWrapper}>
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                                        <circle cx="12" cy="10" r="3"></circle>
                                                    </svg>
                                                    <span>{item?.currentLocation}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.cardMeta}>
                                            <span className={styles.timeStamp}>
                                                {humanReadableTimePassed(item?.updatedOn)}
                                            </span>
                                        </div>
                                    </div>

                                    <div className={styles.cardActions}>
                                        <div className={styles.executiveIdentifier} >
                                            {getExecutiveFullName(item?.executiveIdentifier)}'s candidate has
                                        </div>
                                        {item?.statusMarked === "interested" ?
                                            <div className={styles.statusMarked} >
                                                shown interest in this job.
                                            </div>
                                            :
                                            <div className={styles.statusMarked} >
                                                requested for {item?.statusMarked} immediately.
                                            </div>
                                        }
                                        <Link
                                            to={`/create-applications/${item?.jobIdentifier}`}
                                            className={styles.viewDetailsBtn}
                                        >
                                            View Details
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="9 18 15 12 9 6"></polyline>
                                            </svg>
                                        </Link>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className={styles.emptyState}>
                                <div className={styles.emptyIcon}>
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                                    </svg>
                                </div>
                                <h3>No responses yet</h3>
                                <p>Responses will appear here when candidates interact with your outreach</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Side - Recent Interests */}
                <div className={styles.rightPanel}>
                    <div className={styles.sidebarCard}>
                        <div className={styles.sidebarHeader}>
                            <div className={styles.sidebarTitleWrapper}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <polyline points="12 6 12 12 16 14"></polyline>
                                </svg>
                                <h3 className={styles.sidebarTitle}>Recent Interests</h3>
                            </div>
                            <div className={styles.autoRefreshBadge}>
                                <span className={styles.pulseIndicator}></span>
                                Auto refresh in 30 seconds
                            </div>
                        </div>

                        <div className={styles.interestsList}>
                            {recentInterests?.length > 0 ? (
                                recentInterests.map((item, index) => (
                                    <div key={index} className={styles.interestItem}>
                                        <div className={styles.interestAvatar}>
                                            {item?.stylist_name?.charAt(0).toUpperCase()}
                                        </div>
                                        <div className={styles.interestDetails}>
                                            <span className={styles.interestName}>{item?.stylist_name}</span>
                                            <span className={styles.interestTime}>{humanReadableTimePassed(item?.updatedOn)}</span>
                                            <span className={styles.interestTime}>{getExecutiveFullName(item?.executiveIdentifier)}</span>
                                        </div>
                                        <div className={styles.newBadge}>{item?.statusMarked}</div>
                                    </div>
                                ))
                            ) : (
                                <div className={styles.emptyInterests}>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                                    </svg>
                                    <p>No recent interests</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default CandidateOutreach